import { useCallback } from 'react';
import {
  trackEvent as trackGAEvent,
  safeClarity,
} from '~/components/organism/CookieBanner/utils/initializeAnalytics';
import useSessionHydration from '../useSessionHydration';
import { cookieConsentStateAtom } from '~/components/organism/CookieBanner/state';
import { useRecoilValue } from 'recoil';
import isLfaApp from '~/util/isLFAApp';

type TrackEventArgs = {
  eventName: string;
  parameters?: Record<string, any>;
};

/**
 * Hook that provides a trackEvent function with automatically included user_id and account_id.
 * Also tracks the event with Clarity.
 *
 * @example
 * ```tsx
 * const trackEvent = useTrackEvent();
 *
 * trackEvent({
 *   eventName: 'button_clicked',
 *   parameters: {
 *     button_name: 'submit',
 *   },
 * });
 * ```
 */
const useTrackEvent = () => {
  const [data] = useSessionHydration();
  const consentState = useRecoilValue(cookieConsentStateAtom);
  const isLfa = isLfaApp();

  const meId = data?.me?.id;
  const accountId = data?.account?.id;

  const trackEvent = useCallback(
    ({ eventName, parameters }: TrackEventArgs) => {
      // We don't want to track events in LFA app
      if (isLfa) return;

      // We don't want to track events if we don't have a user or account id
      if (!meId || !accountId) return;

      if (consentState.analytics_storage === 'denied') return;

      safeClarity('event', eventName);

      trackGAEvent({
        eventName,
        parameters: {
          ...parameters,
          account_id: accountId,
          // Use user_identifier as event parameter since user_id is reserved in GA4
          // and doesn't show up in event parameters. The actual user_id is still set at the config level
          user_identifier: meId,
        },
      });
    },

    [consentState.analytics_storage, accountId, meId, isLfa],
  );

  return trackEvent;
};

export default useTrackEvent;
